import { on, createReducer, createFeature } from '@ngrx/store';
import { LayoutActions, DateOfStayActions, MappedAreaActions } from '../actions';
import { Layout } from '../../../objects/layout';
import { LayoutPosition } from '../../../objects/layout-unit-position';
import { Landmark } from 'src/objects/layout-landmark';
import { Section } from '../../../objects/section';
import { Product } from 'src/objects/product';
import { InventoryUnit } from 'src/objects/inventory-unit';
import { TemplatedLayoutPosition } from 'src/objects/layout-position';

export interface State {
  layouts: Layout[] | null;
  layout: Layout | null;
  landmarks: Landmark[];
  layoutsLoading: boolean;
  sections: Section[];
  error: Error | null;
  validInventoryUnitIds: number[] | null;
  selectedLayoutUnitPositions: LayoutPosition[] | null;
}

export const initialState: State = {
  layouts: null,
  layout: null,
  layoutsLoading: false,
  landmarks: [],
  sections: [],
  error: null,
  validInventoryUnitIds: null,
  selectedLayoutUnitPositions: null,
};

export const layoutFeature = createFeature({
  name: 'layout',
  reducer: createReducer(
    initialState,
    on(LayoutActions.loadTemplatedLayoutPositionsWithInventoryUnits, state => ({
      ...state,
      layouts: null,
      layout: null,
      sections: [],
      layoutsLoading: true,
      selectedLayoutUnitPositions: null,
      error: null,
    })),
    on(LayoutActions.loadTemplatedLayoutPositionsWithInventoryUnitsSuccess, (state, { positions, inventoryUnits, products, sections }) => {

      const inventoryUnitById = [...inventoryUnits]
        .sort((a, b) => +a.id - +b.id || a.state.localeCompare(b.state))
        .reduce((acc, iu) => {
          (acc[iu.id] = acc[iu.id] || []).push(iu);
          return acc;
        }, {} as { [id: number]: InventoryUnit[] });


        const productsById = products.reduce((acc, prod) => {
          acc[prod.id] = prod;
          return acc;
        }, {} as { [id: number]: Product });

        const layouts = [...positions]
          .sort((a, b) => a.layout_id - b.layout_id || a.y - b.y || a.x - b.x)
          .reduce((prev, curr) => {
            const layoutId = curr.layout_id;
            const layoutGroup = prev.find(([id]) => id === layoutId);

            if (layoutGroup) {
              layoutGroup[1].push(curr);
            } else {
              prev.push([layoutId, [curr]]);
            }

            return prev;
          }, [] as [number, TemplatedLayoutPosition[]][])
          .map(([layoutId, layoutPositions]) => {
            // TemplatedLayoutPosition[] -> LayoutPosition[] mapping
            const layoutPositionsMapped = layoutPositions.map(p => {
              const position = { } as LayoutPosition;
              const inventoryUnit = (inventoryUnitById[p.inventory_unit_id] || [])[0];

              position.inventory_unit_id = p.inventory_unit_id;
              position.state = inventoryUnit?.state;
              position.product_id = inventoryUnit?.product_id;
              position.product = (inventoryUnit?.product_id > 0 ? productsById[inventoryUnit?.product_id] : null) ?? undefined;
              position.highlighted = p.highlight !== undefined && p.highlight !== null;
              position.position_identifier = p.identifier;
              position.id = p.id;
              position.position_x = p.x;
              position.position_y = p.y;
              position.active = p.active;

              return position;
            });

            const layout = {} as Layout;
            layout.id = layoutId;
            layout.reverse_numbering = false;
            layout.layout_unit_positions = layoutPositionsMapped;

            return layout;
          });


      return {
        ...state,
        layouts,
        layout: layouts.length > 0 ? layouts[0] : null,
        sections,
        layoutsLoading: false,
        selectedLayoutUnitPositions: null,
        error: null,
      };
    }),
    on(LayoutActions.loadTemplatedLayoutPositionsWithInventoryUnitsFailure, (state, { error }) => ({
      ...state,
      layouts: null,
      layout: null,
      sections: [],
      layoutsLoading: false,
      selectedLayoutUnitPositions: null,
      error,
    })),

    on(LayoutActions.loadTemplatedLayoutLandmarks, state => ({
      ...state,
      landmarks: [],
    })),
    on(LayoutActions.loadTemplatedLayoutLandmarksFailure, (state, _) => ({
      ...state,
      landmarks: [],
    })),
    on(LayoutActions.loadTemplatedLayoutLandmarksSuccess, (state, {layoutLandmarks}) => ({
      ...state,
      landmarks: layoutLandmarks,
    })),

    on(LayoutActions.selectLayout, (state, { layout }) => ({
      ...state,
      layout,
      selectedLayoutUnitPositions: null,
    })),

    on(LayoutActions.selectLayoutUnitPositions, (state, { layoutUnitPositions }) => ({
      ...state,
      selectedLayoutUnitPositions: layoutUnitPositions,
    })),

    /**
     * DateOfStay, MappedArea changed related actions
     */
    on(DateOfStayActions.setDateOfStay, (_a, _) => ({
      ...initialState,
    })),

    on(MappedAreaActions.setMappedArea, (_a, _) => ({
      ...initialState,
    })),
  ),
});

export const {
  name: layoutFeatureKey,
  reducer,
  selectLayoutState,
  selectLayouts,
  selectLayout,
  selectLandmarks,
  selectLayoutsLoading,
  selectSections,
  selectError: selectLayoutError,
  selectValidInventoryUnitIds,
  selectSelectedLayoutUnitPositions,
} = layoutFeature;
